/**
 * DUAS regras de sinal, uma para cada tela. Nao sao intercambiaveis.
 *
 * | funcao                | tela                          | decide por        | idempotente |
 * |-----------------------|-------------------------------|-------------------|-------------|
 * | `applyDreSign`        | DRE contabil completa          | `metadata` da serie | sim       |
 * | `applyOperatorSign`   | DRE Resumida (`TABLE_VISUALIZATION`) | `(-)` do rotulo | NAO    |
 *
 * A diferenca nao e de estilo: na DRE completa o valor JA e a contribuicao da linha e a coluna se
 * le somando; na Resumida o valor e "o quanto subtrair" e a contribuicao e o oposto dele. Trocar
 * uma pela outra inverte metade da tabela sem erro visivel numa celula isolada.
 */

import _ from "lodash";
import { parseOperator } from "./dre-row-label";

/**
 * O SINAL nas tabelas resumidas (`TABLE_VISUALIZATION`) — outra tela, outra regra.
 *
 * Aqui o valor bruto NAO e a contribuicao da linha: e "o quanto subtrair". As formulas de la fazem
 * `Lucro Bruto = [Receita Liquida] - [Custos]`, entao o que a linha vale para o resultado e
 * `-bruto`. Exibir `-Math.abs(bruto)` parece a mesma coisa e nao e: quebra o mes em que a linha
 * `(-)` vem NEGATIVA — despesa com credito liquido no periodo, que aumenta o lucro.
 *
 * Medido na DRE Resumida da BG9 NORTE, Jun/25: `(-) Despesas` bruta vale -4.063, e o Lucro Liquido
 * sobe de 94.550 para 98.613. Negando, a celula sai `4.063` e a coluna fecha somando. Com
 * `-Math.abs` sairia `(4.063)` e a soma daria 90.487 — a tabela deixaria de bater.
 *
 * O operador tem DUAS fontes e ignorar a segunda deixa a correcao pela metade: nas 51 DREs
 * Resumidas do template da Nucont, `Custos` e `Despesas` declaram `label: "(-)"`, mas
 * `(-) Deducoes da Receita Bruta` tem `label: ""` e carrega o operador no NOME. O template imprime
 * `label + name`, entao na tela as tres parecem iguais.
 *
 * Precedencia: o label vence, porque e a configuracao daquela visualizacao; o nome e o fallback.
 *
 * NAO muda calculo: as expressoes rodam sobre o valor bruto, nao sobre o que a celula mostra.
 *
 * ATENCAO: esta NAO e idempotente — negar duas vezes volta ao valor original. Aplique uma vez so,
 * no ponto em que o valor e lido para exibir. Quem chama hoje monta a linha do zero a cada render
 * e nunca reescreve o ponto; manter assim.
 */
export function applyOperatorSign(value, label, serieName = "") {
    return subtractsInDre(label, serieName) && value ? -value : value;
}

/** Se a linha subtrai, seja pelo label da visualizacao, seja pelo operador embutido no nome. */
export function subtractsInDre(label, serieName = "") {
    const doLabel = String(label ?? "").trim();
    if (doLabel) return doLabel === "(-)";
    return parseOperator(serieName).operator === "-";
}

/**
 * O SINAL de uma linha da DRE contabil.
 *
 * `valueGet({ applyEffect: true })` aplicava `metadata.negative` como `-1 * valor`, herdado de
 * quando a linha chegava com o saldo contabil cru. Hoje o saldo NAO tem sinal unico na base: medido
 * em dez/2025, `(-) CMV e CSP` chega negativo em 22 empresas e POSITIVO em 9 — nessas, as contas
 * parametrizadas para Compras para Estoque sao patrimoniais, de saldo devedor. `-1 * valor` acerta
 * numas e erra nas outras, e era por isso que `(-) CMV e CSP` aparecia positivo embaixo de um
 * `(-) Custos` negativo.
 *
 * Quem denunciava era a propria tabela: A.V., acumulado e A.H. leem `point.m.v`, que o efeito nao
 * tocava, e mostravam `(15,84%)` na MESMA linha cujo valor saia `103.044`.
 *
 * A linha `(-)` e uma linha de CUSTO: ela sempre subtrai. Entao o sinal e declarado, nao deduzido —
 * `-Math.abs`, que acerta nas duas convencoes e mantem a filha somando no mesmo sentido do pai. O
 * preco e conhecido: mes com estorno liquido aparece como custo MENOR, nunca positivo.
 *
 * `positive`/`reducing` seguem como eram (`Math.abs`, normalizacao de magnitude de conta-folha).
 *
 * Diferente de `_effectsApply`, isto e IDEMPOTENTE: aplicar duas vezes da o mesmo resultado. Era a
 * falta dessa propriedade que transformava uma segunda aplicacao em bug.
 */

/**
 * O valor que a linha exibe, a partir do valor CRU do ponto.
 *
 * Recebe as PROPS da serie — `serie.props` na tabela, a serie crua nos relatorios.
 */
export function applyDreSign(value, serieProps) {
    if (!value) return 0;

    const metadata = _.get(serieProps, "metadata", {}) || {};

    if (metadata.negative) return -Math.abs(value);
    if (metadata.positive || metadata.reducing) return Math.abs(value);

    return value;
}
